/**
 * NexusAIRecommend - Sage AI clinician recommendations
 * Shows top matches with natural language rationale
 */
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Route, CheckCircle2, MapPin, Users, Heart,
  Clock, Globe, ShieldCheck, ChevronDown, ChevronUp
} from "lucide-react";

const FIT_ICONS = {
  availability: Users,
  geography: MapPin,
  specialty: Heart,
  urgency: Clock,
  language: Globe,
  continuity: ShieldCheck,
};

const NexusAIRecommend = ({ referralId, token, API, onRoute, referralStatus }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [routing, setRouting] = useState(false);

  const runRecommendation = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/ai-recommend`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.data);
        if (data.data.recommendations?.length > 0) {
          toast.success(`Sage recommends ${data.data.recommendations.length} clinician(s)`);
        }
      } else {
        toast.error(data.detail || "AI recommendation failed");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  const toggleClinician = (id) => {
    setSelectedClinicians(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleRoute = async () => {
    if (selectedClinicians.length === 0) return;
    setRouting(true);
    try {
      const res = await fetch(`${API}/api/nexus/referrals/${referralId}/route`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ clinician_ids: selectedClinicians }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Routed successfully");
        onRoute?.();
      } else toast.error(data.detail || "Routing failed");
    } catch (err) {
      toast.error("Network error");
    } finally {
      setRouting(false);
    }
  };

  return (
    <Card data-testid="nexus-ai-recommend" className="border-purple-200 bg-gradient-to-br from-purple-50/30 to-indigo-50/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            Sage AI Recommendation
          </CardTitle>
          <Button
            size="sm"
            onClick={runRecommendation}
            disabled={loading}
            data-testid="run-ai-recommend-btn"
            className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
          >
            {loading ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Analyzing...</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5 mr-1" /> {result ? "Re-analyze" : "Get Recommendations"}</>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!result && !loading && (
          <p className="text-xs text-slate-500 text-center py-3">
            Click &ldquo;Get Recommendations&rdquo; to have Sage analyze this referral and suggest the best clinician matches with explanations.
          </p>
        )}

        {loading && (
          <div className="flex flex-col items-center py-6 gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            <p className="text-xs text-slate-500">Sage is analyzing the referral and matching data...</p>
          </div>
        )}

        {result && (
          <div className="space-y-3 mt-1">
            {/* Overall Summary */}
            {result.overall_summary && (
              <div className="bg-white/70 border border-purple-100 rounded-lg p-3">
                <p className="text-sm text-slate-800 leading-relaxed">{result.overall_summary}</p>
                <div className="flex gap-2 mt-2 text-[10px] text-slate-500">
                  <span>{result.total_evaluated} evaluated</span>
                  <span>&middot;</span>
                  <span>{result.passed_hard_stops} eligible</span>
                </div>
              </div>
            )}

            {/* Recommendation Cards */}
            {result.recommendations?.length > 0 ? (
              <div className="space-y-2">
                {result.recommendations.map((rec, idx) => {
                  const isSelected = selectedClinicians.includes(rec.clinician_id);
                  const isExpanded = expandedIdx === idx;
                  const isTop = idx === 0;

                  return (
                    <div
                      key={rec.match_id || idx}
                      data-testid={`ai-rec-${rec.clinician_id}`}
                      className={`rounded-lg border transition-all ${
                        isSelected
                          ? "border-purple-400 bg-purple-50/60 shadow-sm"
                          : isTop
                            ? "border-purple-200 bg-white/80"
                            : "border-slate-200 bg-white/60 hover:border-purple-200"
                      }`}
                    >
                      <div
                        className="flex items-center gap-3 p-3 cursor-pointer"
                        onClick={() => toggleClinician(rec.clinician_id)}
                      >
                        {/* Rank badge */}
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          isSelected ? "bg-purple-600 text-white" : isTop ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"
                        }`}>
                          {isTop ? <Sparkles className="w-4 h-4" /> : `#${rec.rank}`}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm text-slate-900">{rec.clinician_name}</span>
                            {isTop && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">Top Pick</Badge>}
                          </div>
                          {/* AI Rationale */}
                          <p className="text-xs text-slate-600 mt-1 leading-relaxed">{rec.ai_rationale}</p>
                        </div>

                        <div className="text-right shrink-0">
                          <p className="text-xl font-bold text-purple-700">{rec.total_score}</p>
                          <p className="text-[10px] text-slate-500">score</p>
                        </div>

                        <button
                          className="p-1 hover:bg-slate-100 rounded"
                          onClick={(e) => { e.stopPropagation(); setExpandedIdx(isExpanded ? null : idx); }}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                        </button>
                      </div>

                      {/* Expanded: Fit scores */}
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-slate-100 pt-2">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {Object.entries(rec.fit_scores || {}).map(([key, val]) => {
                              const Icon = FIT_ICONS[key] || Sparkles;
                              return (
                                <div key={key} className="flex items-center gap-1.5 text-xs">
                                  <Icon className="w-3 h-3 text-slate-400 shrink-0" />
                                  <span className="capitalize text-slate-600">{key}</span>
                                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${val.score >= 70 ? "bg-emerald-500" : val.score >= 40 ? "bg-amber-400" : "bg-red-400"}`}
                                      style={{ width: `${val.score}%` }}
                                    />
                                  </div>
                                  <span className="text-[10px] font-medium text-slate-700">{val.score}</span>
                                </div>
                              );
                            })}
                          </div>
                          {/* Caseload snapshot */}
                          {rec.snapshot && (
                            <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
                              <span>Caseload: {rec.snapshot.clinician_caseload || 0}/{rec.snapshot.clinician_max_caseload || 25}</span>
                              {rec.snapshot.waitlist_count > 0 && <span>Waitlist: {rec.snapshot.waitlist_count}</span>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Route action */}
                {referralStatus !== "placed" && (
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={handleRoute}
                      disabled={routing || selectedClinicians.length === 0}
                      data-testid="ai-route-btn"
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {routing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Route className="w-3.5 h-3.5 mr-1" />}
                      Route to Selected ({selectedClinicians.length})
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500 text-center py-2">
                {result.overall_summary || "No eligible clinicians found."}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NexusAIRecommend;
